/**
 * @author      Michael Hettmer <mail@michael-hettmer.de>
 * @copyright   2019 Plusbyte UG (haftungsbeschränkt)
 * @license     {@link https://plusbyte.de Plusbyte License}
 */

import React, { memo } from 'react';

import { useFirebaseAnalytics } from '~/apis/useFirebase';
import {
  horseColorIndex,
  HorseColorValues,
  selectCurrentHorse,
  selectCurrentState,
  selectHorse,
  selectHorseCardDeckCurrentMeta,
  selectHorseDropped,
  selectHorseHandi,
  selectHorseRanksCorrected,
  selectHorses,
  selectIsInteractiveGameplayState,
  selectIsOwnHorseIndex,
  selectPlayer,
  selectPlayerCountry,
  selectPlayerName,
  selectRound,
  selectTurnDicesPlayerId,
  State,
  useSelector,
} from '~/app';
import CardDeckIcon from '~/components/CardDeckIcon';
import HorseIcon from '~/components/HorseIcon';
import HorseInfo from '~/components/HorseIcon/HorseInfo';
import Turfmaster from '~/server/src/tmmodel';

interface BarTopHorseProps {
  className?: string;
  horseIndexOfPlayer: number;
  horseIndex: number;
  preview?: boolean;
}

/**
 * Each horse in the top bar is represented by this separate React Component to enable rerender on reference changes
 * on horse props like color / rank / handi etc.
 */
const BarTopHorse = ({
  className,
  horseIndexOfPlayer,
  horseIndex,
  preview: isPreview = false,
}: BarTopHorseProps) => {
  const analytics = useFirebaseAnalytics();

  const currentState = useSelector(selectCurrentState);
  const currentRound = useSelector(selectRound);
  const turnDicesPlayerId = useSelector(selectTurnDicesPlayerId);
  const currentHorse = useSelector(selectCurrentHorse);
  const horses = useSelector(selectHorses);
  const horse = useSelector((state: State) => selectHorse(state, horseIndex));
  const horseColor = isPreview ? horseColorIndex.BLACK : horse ? horse.color : 0;
  const horseRealColor = horse?.realColor ?? -1;
  const horseName = isPreview ? '' : (horse?.name ?? '');
  const { cardsCount, hasBonuscards } = useSelector((state) =>
    selectHorseCardDeckCurrentMeta(state, horseIndex),
  );
  const horsePlayerId = horse ? horse.playerId : -1;
  const isDropped = useSelector((state: State) => selectHorseDropped(state, horseIndex));
  const { totalRank } = useSelector((state) => selectHorseRanksCorrected(state, horseIndex));
  const horseHandi = useSelector((state: State) => selectHorseHandi(state, horseIndex));
  const playerName = useSelector((state: State) => selectPlayerName(state, horsePlayerId));
  const playerCountry = useSelector((state: State) => selectPlayerCountry(state, horsePlayerId));
  const player = useSelector((state: State) => selectPlayer(state, horsePlayerId));

  const isOwnHorse = useSelector((state) => selectIsOwnHorseIndex(state, horseIndex));

  const turnDicesPlayerHorsesIndex = horses
    .filter((horse) => horse.playerId === turnDicesPlayerId)
    .map((horse) => horses.indexOf(horse));

  const isInteractiveGameplayState = useSelector(selectIsInteractiveGameplayState);
  const selected =
    isInteractiveGameplayState &&
    // prevent horse being selected while last turn in round is shown with a delay
    currentState !== Turfmaster.stateMachine.SELECTMOVEOPP &&
    // prevent horse being selected before first and after last round
    currentRound >= 0 &&
    currentRound < Turfmaster.rounds &&
    (currentState === Turfmaster.stateMachine.SELECTDICES
      ? turnDicesPlayerHorsesIndex.includes(horseIndex)
      : horseIndex === currentHorse?.index) && // TODO: undefined check has to be forced
    !isDropped &&
    !isPreview;

  return (
    <div
      key={HorseColorValues[horseColor] + horseIndexOfPlayer}
      className="relative flex items-center justify-around self-start"
    >
      <div
        className={`relative transition-all duration-300 ${
          selected ? 'rounded-full bg-white/30 ring-2 ring-yellow-300/40' : ''
        } ${isPreview ? 'animate-pulse opacity-50' : ''}`}
      >
        <HorseIcon
          {...{ color: horseColor, isDropped, isPreview, selected }}
          className={className}
          onOpenToolTip={() => analytics?.logEvent('show_horse_info', { isOwnHorse })}
        >
          <HorseInfo
            {...{ playerName, horseName, isPreview, isDropped }}
            country={playerCountry}
            horseTotalRank={currentRound > 0 ? totalRank : undefined}
            handicap={Turfmaster.handi[horseHandi]}
            jockeyName={horse?.jockeyName}
            userId={player?.userId}
            {...(horseColor !== horseRealColor && {
              horseRealColor: HorseColorValues[horseRealColor],
            })}
          />
        </HorseIcon>
        {cardsCount !== undefined ? (
          <div className="absolute -bottom-1 left-1/2 z-10 flex -translate-x-1/2 items-center rounded-full border border-black/10 bg-white px-1 py-0 text-[10px] font-light drop-shadow-xs">
            <CardDeckIcon className="h-2.5 w-2.5" />
            <span className="ml-0.5">
              {cardsCount}
              {hasBonuscards && '+'}
            </span>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default memo(BarTopHorse);
